import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { AlertCircle, Mic, RefreshCw, Upload, Square, Play, Radio, FileAudio, RotateCcw, ShieldAlert, ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AudioFileSource,
  CanonicalFrameStreamer,
  DEFAULT_AUDIO_FRAME_MS,
  DEFAULT_AUDIO_SAMPLE_RATE,
  computeFrameBytes,
  MicrophoneSource,
} from '../lib/liveAudioPipeline';

const BACKEND_HTTP = process.env.REACT_APP_TRANSCRIPT_HTTP_URL || 'http://127.0.0.1:8000';

const resolveWebSocketUrl = () => {
  const explicit = process.env.REACT_APP_TRANSCRIPT_WS_URL;
  const pageIsHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

  if (explicit) {
    if (pageIsHttps && explicit.startsWith('ws://')) {
      return explicit.replace('ws://', 'wss://');
    }
    return explicit;
  }

  try {
    const base = new URL(BACKEND_HTTP);
    const wsProtocol = pageIsHttps ? 'wss:' : base.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${base.host}/ws/audio-stream`;
  } catch (_error) {
    return pageIsHttps ? 'wss://127.0.0.1:8000/ws/audio-stream' : 'ws://127.0.0.1:8000/ws/audio-stream';
  }
};

const BACKEND_WS = resolveWebSocketUrl();

const PHASE_TRANSITIONS = {
  idle: ['preparing', 'error'],
  preparing: ['connecting', 'idle', 'error'],
  connecting: ['streaming', 'stopping', 'error'],
  streaming: ['stopping', 'error'],
  stopping: ['done', 'idle', 'error'],
  done: ['idle', 'preparing', 'error'],
  error: ['idle', 'preparing'],
};

const initialState = {
  phase: 'idle',
  source: 'microphone',
  selectedFile: null,
  sessionId: null,
  stitchedText: '',
  debugRows: [],
  failureMarkers: [],
  insights: [],
  uploadProgress: 0,
  permission: 'unknown',
  connectionLabel: 'Disconnected',
  fastMode: false,
  error: '',
};

const setPhase = (state, next) => {
  const allowed = PHASE_TRANSITIONS[state.phase] || [];
  if (!allowed.includes(next) && state.phase !== next) {
    return state;
  }
  return { ...state, phase: next };
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_SOURCE':
      return {
        ...state,
        source: action.payload,
        selectedFile: action.payload === 'audio-file' ? state.selectedFile : null,
      };
    case 'SELECT_FILE':
      return { ...state, selectedFile: action.payload, uploadProgress: 0 };
    case 'SET_FAST_MODE':
      return { ...state, fastMode: action.payload };
    case 'SET_PERMISSION':
      return { ...state, permission: action.payload };
    case 'PREPARE':
      return {
        ...setPhase(state, 'preparing'),
        error: '',
        debugRows: [],
        failureMarkers: [],
        insights: [],
        sessionId: null,
        uploadProgress: 0,
        connectionLabel: 'Preparing audio source',
      };
    case 'SET_SESSION_ID':
      return {
        ...state,
        sessionId: action.payload || state.sessionId,
      };
    case 'CONNECTING':
      return { ...setPhase(state, 'connecting'), connectionLabel: 'Connecting to backend' };
    case 'STREAMING':
      return { ...setPhase(state, 'streaming'), connectionLabel: 'Streaming live frames' };
    case 'STOPPING':
      return { ...setPhase(state, 'stopping'), connectionLabel: 'Stopping session' };
    case 'DONE':
      return { ...setPhase(state, 'done'), connectionLabel: 'Session complete' };
    case 'ERROR':
      return {
        ...setPhase(state, 'error'),
        error: action.payload || 'Unexpected failure',
        connectionLabel: 'Error',
      };
    case 'SET_IDLE':
      return { ...setPhase(state, 'idle'), connectionLabel: 'Disconnected' };
    case 'SET_PROGRESS':
      return { ...state, uploadProgress: action.payload };
    case 'TRANSCRIPT_MESSAGE': {
      const { payload } = action;
      const nextRows = [
        {
          id: `${payload.sequence_id ?? 'na'}-${Date.now()}-${Math.random()}`,
          sequenceId: payload.sequence_id ?? '-',
          deltaText: payload.delta_text || '',
          status: payload.status || '-',
          errorCode: payload.error_code || '',
          at: new Date().toLocaleTimeString(),
        },
        ...state.debugRows,
      ].slice(0, 250);

      const failed = payload.error_code || (payload.status && payload.status.toLowerCase().includes('fail'));
      const nextMarkers = failed
        ? [
            {
              id: `${payload.sequence_id ?? 'na'}-${Date.now()}`,
              sequenceId: payload.sequence_id ?? '-',
              status: payload.status || 'error',
              errorCode: payload.error_code || 'unknown_error',
            },
            ...state.failureMarkers,
          ].slice(0, 25)
        : state.failureMarkers;

      return {
        ...state,
        sessionId: payload.session_id || state.sessionId,
        stitchedText: payload.full_text || state.stitchedText,
        debugRows: nextRows,
        failureMarkers: nextMarkers,
      };
    }
    case 'SET_INSIGHTS':
      return { ...state, insights: action.payload || [] };
    case 'APPEND_INSIGHT': {
      const next = action.payload;
      if (!next) return state;
      const exists = state.insights.some(
        (item) =>
          item.session_id === next.session_id &&
          item.source_sequence_id === next.source_sequence_id &&
          item.created_at_ms === next.created_at_ms,
      );
      if (exists) return state;
      return { ...state, insights: [...state.insights, next] };
    }
    case 'CLEAR_INSIGHTS':
      return { ...state, insights: [] };
    case 'RESET_UI':
      return {
        ...state,
        stitchedText: '',
        debugRows: [],
        failureMarkers: [],
        insights: [],
        sessionId: null,
        uploadProgress: 0,
        error: '',
      };
    case 'RESET_ALL':
      return {
        ...initialState,
        source: state.source,
        selectedFile: state.selectedFile,
      };
    default:
      return state;
  }
}

const LiveVoiceDemo = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const wsRef = useRef(null);
  const sourceRef = useRef(null);
  const streamerRef = useRef(null);
  const closedByClientRef = useRef(false);
  const phaseRef = useRef(initialState.phase);
  const sessionIdRef = useRef(initialState.sessionId);
  const lastInsightsSessionRef = useRef(null);
  const insightsScrollRef = useRef(null);

  const config = useMemo(() => {
    const sampleRate = DEFAULT_AUDIO_SAMPLE_RATE;
    const frameMs = DEFAULT_AUDIO_FRAME_MS;

    return {
      sampleRate,
      frameMs,
      frameBytes: computeFrameBytes(sampleRate, frameMs),
    };
  }, []);

  const isBusy = ['preparing', 'connecting', 'streaming', 'stopping'].includes(state.phase);

  useEffect(() => {
    phaseRef.current = state.phase;
    sessionIdRef.current = state.sessionId;
  }, [state.phase, state.sessionId]);

  useEffect(() => {
    if (!insightsScrollRef.current) return;
    insightsScrollRef.current.scrollTop = insightsScrollRef.current.scrollHeight;
  }, [state.insights.length]);

  const cleanupRuntime = async ({ clearUi = false } = {}) => {
    closedByClientRef.current = true;
    lastInsightsSessionRef.current = null;

    if (sourceRef.current) {
      await sourceRef.current.stop();
      sourceRef.current = null;
    }

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close(1000, 'client_stop');
      }
      wsRef.current = null;
    }

    if (streamerRef.current) {
      streamerRef.current.reset();
      streamerRef.current = null;
    }

    if (clearUi) {
      dispatch({ type: 'RESET_UI' });
      dispatch({ type: 'SET_IDLE' });
    }
  };

  const fetchTranscriptSnapshot = async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${BACKEND_HTTP}/transcripts/${sessionId}`);
      if (!response.ok) return;
      const payload = await response.json();
      if (payload?.text) {
        dispatch({
          type: 'TRANSCRIPT_MESSAGE',
          payload: {
            session_id: payload.session_id,
            sequence_id: 'snapshot',
            delta_text: '',
            full_text: payload.text,
            status: 'snapshot',
            error_code: '',
          },
        });
      }
    } catch (error) {
      // best effort
    }
  };

  const fetchInsightHistory = async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${BACKEND_HTTP}/insights/${sessionId}`);
      if (!response.ok) return;
      const payload = await response.json();
      if (Array.isArray(payload?.insights)) {
        const deduped = [];
        const keys = new Set();
        payload.insights.forEach((item) => {
          const key = `${item.session_id}-${item.source_sequence_id}-${item.created_at_ms}`;
          if (!keys.has(key)) {
            keys.add(key);
            deduped.push(item);
          }
        });
        dispatch({ type: 'SET_INSIGHTS', payload: deduped });
      }
    } catch (error) {
      // best effort
    }
  };

  const handleStart = async () => {
    if (isBusy) return;
    dispatch({ type: 'PREPARE' });
    lastInsightsSessionRef.current = null;

    try {
      await cleanupRuntime();
      closedByClientRef.current = false;

      let source;
      if (state.source === 'microphone') {
        source = new MicrophoneSource(config.sampleRate);
      } else {
        if (!state.selectedFile) {
          throw new Error('Select an audio file before starting file mode.');
        }
        source = new AudioFileSource(state.selectedFile, config.sampleRate, config.frameMs);
      }

      sourceRef.current = source;

      try {
        await source.prepare();
        if (state.source === 'microphone') {
          dispatch({ type: 'SET_PERMISSION', payload: 'granted' });
        }
      } catch (error) {
        if (state.source === 'microphone') {
          dispatch({ type: 'SET_PERMISSION', payload: 'denied' });
        }
        if (error?.name === 'NotAllowedError') {
          throw new Error('Microphone permission denied. Allow access and retry.');
        }
        if (state.source === 'audio-file') {
          throw new Error('Audio decode/resample failed. Use a valid WAV/MP3/M4A file and retry.');
        }
        throw error;
      }

      dispatch({ type: 'CONNECTING' });

      const ws = new WebSocket(BACKEND_WS);
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;

      ws.onmessage = (message) => {
        try {
          const payload = JSON.parse(message.data);
          if (payload.event === 'session.started' && payload.session_id) {
            dispatch({ type: 'SET_SESSION_ID', payload: payload.session_id });
            if (lastInsightsSessionRef.current !== payload.session_id) {
              lastInsightsSessionRef.current = payload.session_id;
              fetchInsightHistory(payload.session_id);
            }
            return;
          }
          if (payload.event === 'transcript.text') {
            dispatch({ type: 'TRANSCRIPT_MESSAGE', payload });
            if (payload.session_id && lastInsightsSessionRef.current !== payload.session_id) {
              lastInsightsSessionRef.current = payload.session_id;
              fetchInsightHistory(payload.session_id);
            }
            return;
          }
          if (payload.event === 'insight.summary') {
            dispatch({
              type: 'APPEND_INSIGHT',
              payload: {
                session_id: payload.session_id,
                source_sequence_id: payload.source_sequence_id,
                summary: payload.summary,
                created_at_ms: payload.created_at_ms,
              },
            });
            if (payload.session_id) {
              dispatch({ type: 'SET_SESSION_ID', payload: payload.session_id });
              lastInsightsSessionRef.current = payload.session_id;
            }
          }
        } catch (error) {
          dispatch({ type: 'ERROR', payload: 'Received non-JSON backend event.' });
        }
      };

      ws.onerror = () => {
        dispatch({
          type: 'ERROR',
          payload:
            'WebSocket connection failed. If this site is HTTPS, backend must expose WSS (TLS) for /ws/audio-stream.',
        });
      };

      ws.onclose = () => {
        const wasClientClose = closedByClientRef.current;
        if (!wasClientClose && phaseRef.current !== 'stopping') {
          dispatch({ type: 'ERROR', payload: 'WebSocket closed unexpectedly.' });
          return;
        }

        dispatch({ type: 'DONE' });
      };

      ws.onopen = async () => {
        dispatch({ type: 'STREAMING' });

        streamerRef.current = new CanonicalFrameStreamer({
          sampleRate: config.sampleRate,
          frameMs: config.frameMs,
          onFrame: (frameBuffer) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(frameBuffer);
            }
          },
        });

        if (state.source === 'microphone') {
          await source.start((chunk, inputRate) => {
            streamerRef.current?.push(chunk, inputRate);
          });
          return;
        }

        const completed = await source.start(
          (chunk, inputRate) => {
            streamerRef.current?.push(chunk, inputRate);
          },
          (progress) => dispatch({ type: 'SET_PROGRESS', payload: progress }),
          state.fastMode,
        );

        if (completed) {
          dispatch({ type: 'STOPPING' });
          closedByClientRef.current = true;
          ws.close(1000, 'file_complete');
          await fetchTranscriptSnapshot(sessionIdRef.current);
        }
      };
    } catch (error) {
      await cleanupRuntime();
      dispatch({ type: 'ERROR', payload: error.message || 'Unable to start streaming.' });
    }
  };

  const handleStop = async () => {
    if (!['connecting', 'streaming'].includes(state.phase)) return;

    dispatch({ type: 'STOPPING' });

    try {
      if (sourceRef.current) {
        await sourceRef.current.stop();
      }

      if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) {
        closedByClientRef.current = true;
        wsRef.current.close(1000, 'manual_stop');
      }

      await fetchTranscriptSnapshot(sessionIdRef.current);
    } catch (error) {
      dispatch({ type: 'ERROR', payload: 'Failed to stop cleanly.' });
    }
  };

  const handleReset = async () => {
    try {
      await cleanupRuntime({ clearUi: true });

      if (state.sessionId) {
        await fetch(`${BACKEND_HTTP}/transcripts/${state.sessionId}/reset`, {
          method: 'POST',
        });
        await fetch(`${BACKEND_HTTP}/insights/${state.sessionId}/reset`, {
          method: 'POST',
        });
      }

      dispatch({ type: 'CLEAR_INSIGHTS' });
      dispatch({ type: 'RESET_ALL' });
    } catch (error) {
      dispatch({ type: 'ERROR', payload: 'Reset failed. Backend may be unavailable.' });
    }
  };

  const handleRetry = async () => {
    await cleanupRuntime();
    dispatch({ type: 'SET_IDLE' });
    dispatch({ type: 'RESET_UI' });
    await handleStart();
  };

  useEffect(() => {
    return () => {
      cleanupRuntime();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAF9] pt-32 pb-20 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <section className="lg:col-span-12 border border-teal-100 rounded-2xl p-6 lg:p-8 bg-gradient-to-br from-white to-teal-50/70 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-4 max-w-4xl">
              <Link to="/demos" className="inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-800 font-medium">
                <ArrowLeft size={14} /> Back to demos
              </Link>
              <p className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold tracking-wide rounded-full bg-teal-100 text-teal-700">
                <Radio size={14} />
                Live Demo System
              </p>
              <h1 className="font-serif text-3xl lg:text-5xl text-[#11201E] leading-tight">
                Live Voice Transcription & Insight Engine
              </h1>
              <p className="text-sm sm:text-base text-[#3D5551] leading-relaxed">
                Real-time conversational intelligence demo with microphone and audio-file input. Streams canonical PCM16 mono frames to your backend and renders stitched transcript updates live.
              </p>
            </div>
            <div className="bg-[#11201E] text-white rounded-xl p-4 min-w-[220px]">
              <p className="text-xs uppercase tracking-wider text-teal-100 mb-2">Canonical Audio</p>
              <p className="text-sm">Sample Rate: {config.sampleRate} Hz</p>
              <p className="text-sm">Frame: {config.frameMs} ms</p>
              <p className="text-sm">Bytes/Frame: {config.frameBytes}</p>
            </div>
          </div>
        </section>

        <section className="lg:col-span-4 border border-[#D9E4E1] rounded-2xl p-5 bg-white shadow-sm">
          <h2 className="font-serif text-2xl text-[#1A2E2B] mb-5">Controls</h2>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-[#EEF5F3] border border-[#DCE9E6]">
              <button
                type="button"
                onClick={() => dispatch({ type: 'SET_SOURCE', payload: 'microphone' })}
                disabled={isBusy}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  state.source === 'microphone' ? 'bg-white text-teal-700 shadow-sm' : 'text-[#46635D]'
                }`}
              >
                <Mic size={14} className="inline mr-1" /> Microphone
              </button>
              <button
                type="button"
                onClick={() => dispatch({ type: 'SET_SOURCE', payload: 'audio-file' })}
                disabled={isBusy}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  state.source === 'audio-file' ? 'bg-white text-teal-700 shadow-sm' : 'text-[#46635D]'
                }`}
              >
                <FileAudio size={14} className="inline mr-1" /> Audio File
              </button>
            </div>

            {state.source === 'audio-file' && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#47625D] uppercase tracking-wider">Input Audio</label>
                <input
                  type="file"
                  accept="audio/*"
                  disabled={isBusy}
                  onChange={(event) => dispatch({ type: 'SELECT_FILE', payload: event.target.files?.[0] || null })}
                  className="w-full text-sm text-[#38524D] file:mr-3 file:px-3 file:py-2 file:rounded-md file:border file:border-[#C8D8D4] file:bg-[#F3FAF8]"
                />
                <p className="text-xs text-[#5A7270] truncate">
                  {state.selectedFile ? `Selected: ${state.selectedFile.name}` : 'No file selected'}
                </p>
                <label className="inline-flex items-center gap-2 text-xs text-[#446460]">
                  <input
                    type="checkbox"
                    checked={state.fastMode}
                    disabled={isBusy}
                    onChange={(e) => dispatch({ type: 'SET_FAST_MODE', payload: e.target.checked })}
                  />
                  Fast mode (stream without real-time pacing)
                </label>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleStart}
                disabled={isBusy}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:opacity-50"
              >
                <Play size={14} /> Start
              </button>
              <button
                type="button"
                onClick={handleStop}
                disabled={!['connecting', 'streaming'].includes(state.phase)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1f2937] text-white rounded-lg text-sm font-semibold hover:bg-[#111827] disabled:opacity-50"
              >
                <Square size={14} /> Stop
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#CCDCD8] text-[#24413D] rounded-lg text-sm font-semibold hover:bg-[#F3FAF8]"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#CCDCD8] text-[#24413D] rounded-lg text-sm font-semibold hover:bg-[#F3FAF8]"
              >
                <RefreshCw size={14} /> Retry
              </button>
            </div>

            <div className="rounded-xl border border-[#D7E3E0] p-3 bg-[#F7FBFA]">
              <p className="text-xs uppercase tracking-wider text-[#5A716D] mb-2">Connection Status</p>
              <p className="text-sm font-semibold text-[#1E3733]">{state.connectionLabel}</p>
              <p className="text-xs text-[#55706A] mt-1">Phase: {state.phase}</p>
              <p className="text-xs text-[#55706A]">Session: {state.sessionId || 'pending'}</p>
              <p className="text-xs text-[#55706A]">Mic Permission: {state.permission}</p>
            </div>

            {state.source === 'audio-file' && (
              <div className="rounded-xl border border-[#D7E3E0] p-3">
                <div className="flex items-center justify-between mb-2 text-xs text-[#4D6761]">
                  <span className="inline-flex items-center gap-1"><Upload size={12} /> Upload/Stream Progress</span>
                  <span>{Math.round(state.uploadProgress * 100)}%</span>
                </div>
                <div className="w-full h-2 bg-[#E7F0EE] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
                    style={{ width: `${Math.round(state.uploadProgress * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {state.error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <p className="font-semibold inline-flex items-center gap-2"><AlertCircle size={14} /> Runtime Error</p>
                <p className="mt-1">{state.error}</p>
              </div>
            )}
          </div>
        </section>

        <section className="lg:col-span-8 border border-[#D9E4E1] rounded-2xl p-5 bg-white shadow-sm">
          <h2 className="font-serif text-2xl text-[#1A2E2B] mb-4">Live Stitched Transcript</h2>
          <div className="rounded-xl border border-[#DCE7E4] bg-[#FBFEFD] p-4 min-h-[240px]">
            {state.stitchedText ? (
              <p className="text-sm sm:text-base leading-relaxed text-[#1E3430] whitespace-pre-wrap">
                {state.stitchedText}
              </p>
            ) : (
              <p className="text-sm text-[#617975]">Transcript will appear here after backend starts emitting transcript events.</p>
            )}

            {state.failureMarkers.length > 0 && (
              <div className="mt-4 border-t border-red-100 pt-3">
                <p className="text-xs font-semibold text-red-700 mb-2 inline-flex items-center gap-1">
                  <ShieldAlert size={12} /> Failure Markers
                </p>
                <div className="flex flex-wrap gap-2">
                  {state.failureMarkers.map((marker) => (
                    <span key={marker.id} className="text-[11px] px-2 py-1 rounded bg-red-50 text-red-700 border border-red-100">
                      seq {marker.sequenceId} • {marker.status} • {marker.errorCode}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="lg:col-span-12 border border-[#D9E4E1] rounded-2xl p-5 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="font-serif text-2xl text-[#1A2E2B] inline-flex items-center gap-2">
              <Sparkles size={18} className="text-teal-600" /> Live Insights
            </h2>
            <p className="text-xs text-[#607A75]">
              Insights appear only after trigger thresholds; gaps are expected.
            </p>
          </div>
          <div
            ref={insightsScrollRef}
            className="rounded-xl border border-[#DCE7E4] bg-[#FBFEFD] p-3 max-h-[320px] overflow-y-auto space-y-2"
          >
            {state.insights.length === 0 && (
              <p className="text-sm text-[#617975]">No insights yet. Keep speaking/uploading audio.</p>
            )}
            {state.insights.map((item, index) => (
              <div key={`${item.session_id}-${item.source_sequence_id}-${item.created_at_ms}`} className="rounded-lg border border-[#E4EEEB] bg-white px-3 py-2">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="text-xs font-semibold text-[#1F3D39]">#{index + 1}</p>
                  <p className="text-[11px] text-[#607A75]">
                    {item.created_at_ms ? new Date(item.created_at_ms).toLocaleTimeString() : '--:--:--'}
                  </p>
                </div>
                <p className="text-sm text-[#28443F] leading-relaxed">{item.summary}</p>
                <p className="text-[11px] text-[#607A75] mt-1">source sequence: {item.source_sequence_id}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="lg:col-span-12 border border-[#D9E4E1] rounded-2xl p-5 bg-white shadow-sm">
          <h2 className="font-serif text-2xl text-[#1A2E2B] mb-4">Segment / Debug Timeline</h2>
          <div className="rounded-xl border border-[#DCE7E4] overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#F4F9F8] text-[11px] font-semibold uppercase tracking-wider text-[#4E6762]">
              <span className="col-span-2">Sequence</span>
              <span className="col-span-5">Delta Text</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-2">Error</span>
              <span className="col-span-1">Time</span>
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              {state.debugRows.length === 0 && (
                <div className="px-3 py-5 text-sm text-[#617975]">No events yet. Start a stream to inspect transcript deltas.</div>
              )}
              {state.debugRows.map((row) => (
                <div
                  key={row.id}
                  className={`grid grid-cols-12 gap-2 px-3 py-2 text-xs border-t border-[#EDF3F1] ${
                    row.errorCode ? 'bg-red-50/60' : 'bg-white'
                  }`}
                >
                  <span className="col-span-2 text-[#2B4541] font-mono">{row.sequenceId}</span>
                  <span className="col-span-5 text-[#28443F] truncate" title={row.deltaText}>{row.deltaText || '-'}</span>
                  <span className="col-span-2 text-[#3D5A55]">{row.status || '-'}</span>
                  <span className={`col-span-2 ${row.errorCode ? 'text-red-700 font-semibold' : 'text-[#4D6761]'}`}>{row.errorCode || '-'}</span>
                  <span className="col-span-1 text-[#607A75]">{row.at}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LiveVoiceDemo;
